import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { PORTAL_UNEXPECTED, toPortalFailure, type PortalFailure } from "../lib/errors";
import { chatMessagesKey } from "./portalQueries";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";
import { requestThreadKey, requestsKey } from "./requestQueries";

/**
 * Bilagor, both directions, for both client surfaces. plan.md sections 5.2 and 5.3.
 *
 * **What was measured on 2026-09-12, before any of this existed.** `/api/files/upload-intent` had no
 * caller anywhere in `app/`, which `tests/worker-route-reachability.test.ts` listed as one of three
 * known defects. The word `attachment` appeared zero times in `app/data/requestQueries.ts` and
 * `app/data/portalQueries.ts`. The only attachment read path in the whole application was Petrovic's,
 * `useAdminAttachmentDownload` in `app/data/adminClientQueries.ts`. So every attachment in the
 * database had arrived through a test fixture, and a client had the right to attach a file and no way
 * to exercise it.
 *
 * Both halves live here rather than in the two surface modules, because a chat message and a request
 * comment are the same file flow with a different parent column. `public.attachments` carries
 * `attachments_exactly_one_parent_check`, so the two parents are one shape with two names, and two
 * copies of this flow would be two places to get the `x-portal-tenant` header wrong.
 *
 * ## The order, which the gateways decide and this file does not
 *
 * `public.begin_attachment_upload` (current definition in
 * `20260830190001_phase4b_gateway_parity_and_key_versioning.sql`) raises
 * `PORTAL_ATTACHMENT_PARENT_REQUIRED` unless exactly one of `p_message_id` and
 * `p_request_comment_id` is supplied. **The parent has to exist before the bytes do.** So the message
 * or the comment is written first, through its own gateway, and the files follow it.
 *
 * That ordering has a cost and it is written down rather than hidden: a send whose message lands and
 * whose upload fails leaves the message in the thread without its file. The mutation below therefore
 * reports per file rather than throwing for the set, so the surface can say exactly that and offer a
 * retry of only the files that did not land.
 *
 * ## The read half needs no gateway and no migration, which was checked rather than assumed
 *
 * `attachments_select_member` is a permissive SELECT policy on `public.attachments` for
 * `authenticated`, scoped to `public.current_tenant_ids()`, and migration
 * `20260829120005_chat_requests_attachments.sql` grants exactly nine columns to that role:
 * `id, tenant_id, message_id, request_comment_id, display_filename, media_type, byte_size,
 * availability_status, created_at`. Every column this surface needs is in that list and nothing it
 * must not see is. `storage_object_id`, `uploaded_by_user_id`, `data_role`, `retention_class` and
 * `retain_until` are all ungranted, so a select naming one of them is refused by the column ACL
 * rather than by a view somebody remembered to write. The narrowest read model already exists, so no
 * migration was added and no grant was widened. `tests/db/phase7-client-attachments.test.ts` asserts
 * the grant from the catalogue, so this paragraph stays a measurement.
 *
 * The uploader's name is the one thing the client's read genuinely cannot have, and it is left out
 * rather than approximated. Petrovic's own `admin_client_attachments` computes
 * `uploaded_by_is_admin` from `private.admin_users`, which a client role has no USAGE on. The list
 * therefore names the file and not the person, and the message or comment above it already says who
 * wrote it.
 */

/* -------------------------------------------------------------------------- */
/* 1. Vad som får bifogas                                                      */
/* -------------------------------------------------------------------------- */

/**
 * What `worker/src/files/signatures.ts` accepts, restated because `app/` and `worker/` are separate
 * TypeScript projects and neither imports the other.
 *
 * `app/data/adminDocumentWriteQueries.ts` already carries the same list as `PUBLISHABLE_MEDIA_TYPES`
 * and the same ceiling as `MAX_UPLOAD_BYTES`, and importing them from there was the first thing
 * tried and then rejected. That module imports `./adminClientQueries`, which is the largest data
 * module in the repository and reaches the browser only through the lazy chunks in
 * `app/routes/adminChunks.ts`. A client-side import of one constant from it would pull the whole
 * admin graph into the bundle every client downloads before the sign-in form renders, which is the
 * exact cost that file split was measured to remove. The right home for both values is `app/lib`,
 * and there is still no shared module there.
 *
 * This copy is a courtesy and never the boundary. architecture.md section 9 calls the allow-list a
 * security boundary rather than a UI hint, and the Worker reads the leading bytes and refuses a file
 * whose content does not match what was declared, so a browser that lies about a type is refused
 * there. What the copy buys is a refusal that names the file in front of the person instead of a 415
 * arriving after 20 MiB have crossed the wire on a phone.
 *
 * The database holds the same eight values a third time, in
 * `attachments_media_type_allow_list_check`. Three copies of one list is not a design; it is what a
 * boundary between three runtimes costs, and every copy says so on its face.
 */
export const ATTACHABLE_MEDIA_TYPES = [
  "application/pdf",
  "text/plain",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
] as const;

/** `MAX_UPLOAD_BYTES` in `worker/src/env.ts`, and plan.md section 9, "Cost controls": 20 MiB. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/**
 * plan.md section 9, "Cost controls": max five attachments per ärende.
 *
 * `begin_attachment_upload` counts `pending` and `available` rows against the same parent and raises
 * `PORTAL_ATTACHMENT_LIMIT_REACHED` at five, so the ceiling is per message and per comment rather
 * than per request. A composer only ever builds one new parent, so counting what is in the composer
 * is the whole of the browser's half. A retry after a partial failure is the one case where the
 * count matters, and it is why the failed files stay selected rather than being cleared.
 */
export const MAX_ATTACHMENTS_PER_PARENT = 5;

/**
 * The `accept` attribute, built from the same list.
 *
 * It is a hint to the file picker and nothing more. A person can always choose "all files" in the
 * operating system's dialog, which is why the refusal below exists and why the Worker's signature
 * check exists behind that.
 */
export const ATTACHMENT_ACCEPT = ATTACHABLE_MEDIA_TYPES.join(",");

/** The eight values, said as the words a client uses. Same map as Petrovic's side of the portal. */
export const ATTACHMENT_MEDIA_TYPE_LABEL: Record<string, string | undefined> = {
  "application/pdf": "PDF",
  "text/plain": "Textfil",
  "text/csv": "CSV",
  "image/png": "PNG-bild",
  "image/jpeg": "JPEG-bild",
  "image/webp": "WebP-bild",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel"
};

/** The five values `attachments_availability_status_check` allows. */
export const ATTACHMENT_STATUS_LABEL: Record<string, string | undefined> = {
  pending: "Kontrolleras",
  available: "Går att öppna",
  quarantined_invalid: "Stoppad i kontrollen",
  returned: "Återlämnad",
  deleted: "Raderad"
};

/**
 * Why a file cannot be opened, said once per status and never as a disabled control.
 *
 * The same decision Petrovic's own list made and for the same reason: a greyed-out knapp says the
 * file is there and the portal is being unhelpful. Four of the five statuses mean different things
 * and a person acts on each one differently. `available` has no entry, because a file that opens has
 * nothing to explain.
 *
 * The wording is the client's rather than the operator's. Petrovic's copy says "be kunden skicka den
 * igen"; this one is addressed to the person who sent the file.
 */
export const ATTACHMENT_BLOCKED_REASON: Record<string, string | undefined> = {
  pending: "Filen kontrolleras fortfarande. Ladda om sidan om en stund.",
  quarantined_invalid:
    "Innehållet stämde inte med filtypen, så filen stoppades i kontrollen. Skicka den igen som PDF, bild, Word, Excel, text eller CSV.",
  returned: "Filen är återlämnad och går inte att öppna här längre.",
  deleted: "Filen är raderad ur lagringen. Raden står kvar för att visa att den har funnits."
};

/** A status this build has never heard of makes no claim about why the file will not open. */
export const ATTACHMENT_UNKNOWN_BLOCKED_REASON =
  "Filen står i ett läge den här vyn inte känner igen, så den går inte att öppna härifrån.";

/**
 * Bytes as a person reads them.
 *
 * `attachments_byte_size_check` caps a row at 20 MiB, so there is no case above megabytes and no
 * reason for a loop over units. Swedish decimal comma.
 *
 * The two thresholds are `formatBytes` in `app/routes/AdminClientsRoute.tsx`, deliberately, so that
 * one file reads the same on the client's screen and on Petrovic's. It is a copy because that
 * helper is module-private and the shared home in `app/lib` does not exist yet.
 */
export function formatAttachmentBytes(bytes: number): string {
  const mib = bytes / (1024 * 1024);

  if (mib >= 1) {
    return `${mib.toFixed(1).replace(".", ",")} MB`;
  }
  return `${Math.round(bytes / 1024)} kB`;
}

/**
 * The browser's own refusal, said about one named file.
 *
 * It is deliberately not a `PORTAL_*` code and deliberately not in `app/lib/errors.ts`. That file's
 * opening rule is that the submitted value is never part of the message, and it is right: a refusal
 * there says what was refused rather than what was sent, because those sentences are shown about an
 * action. This one is shown beside a file the person has just chosen and is still holding, where
 * naming it is the entire point, and where "en fil" would be useless in a list of four.
 *
 * Two refusals only, because two are all the browser can know before sending. Whether the bytes are
 * really a PDF is a question only `worker/src/files/signatures.ts` can answer, and it does.
 */
export function attachmentRefusal(file: File): string | null {
  if (!(ATTACHABLE_MEDIA_TYPES as readonly string[]).includes(file.type)) {
    return "Filtypen tas inte emot. Skicka PDF, PNG, JPEG, WebP, Word, Excel, text eller CSV.";
  }

  if (file.size <= 0) {
    return "Filen är tom och går inte att skicka.";
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Filen är ${formatAttachmentBytes(file.size)}. Taket är 20 MB.`;
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/* 2. Läsningen                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One attachment, as the nine granted columns describe it.
 *
 * `message_id` and `request_comment_id` are both nullable in the table and exactly one of them is
 * set, which `attachments_exactly_one_parent_check` guarantees. The nullability is therefore real on
 * each column and impossible on both at once, and the two hooks below each read one of them.
 */
export interface ClientAttachment {
  id: string;
  message_id: string | null;
  request_comment_id: string | null;
  display_filename: string;
  media_type: string;
  byte_size: number;
  availability_status: string;
  created_at: string;
}

const ATTACHMENT_COLUMNS =
  "id, message_id, request_comment_id, display_filename, media_type, byte_size, availability_status, created_at";

export const chatAttachmentsKey = (tenantId: string) =>
  ["portal", "chat-attachments", tenantId] as const;

/**
 * Build round 54, 2026-10-31: every request's attachments at once. The client's `requests` channel
 * says that some request changed and not which one is open, so it refreshes them all by this prefix.
 */
export const requestAttachmentsPrefix = ["portal", "request-attachments"] as const;

/**
 * A prefix, so the full key below can carry the comment ids without any caller having to know them.
 *
 * React Query matches by prefix, so `invalidateQueries({ queryKey: requestAttachmentsKey(id) })`
 * refreshes whichever comment set is currently loaded for that request.
 */
export const requestAttachmentsKey = (requestId: string) =>
  [...requestAttachmentsPrefix, requestId] as const;

/** Attachments grouped by the parent they hang from, which is how both surfaces render them. */
export type AttachmentsByParent = ReadonlyMap<string, ClientAttachment[]>;

function groupBy(
  rows: ClientAttachment[],
  parent: (row: ClientAttachment) => string | null
): AttachmentsByParent {
  const grouped = new Map<string, ClientAttachment[]>();

  for (const row of rows) {
    const key = parent(row);
    if (!key) {
      continue;
    }
    const existing = grouped.get(key);
    if (existing) {
      existing.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }

  return grouped;
}

/**
 * Every file hanging off a message in this tenant's one thread.
 *
 * One request for the whole log rather than one per message, because `useChatMessages` reads 200
 * messages and 200 round trips to draw a paperclip is not a design. The filter is
 * `message_id is not null`, which is what separates the chat's files from the ärenden's inside a
 * table that holds both; the tenant scope is the RLS policy's and is never sent from here.
 *
 * 500 rather than 200: five files per message is the ceiling, so a log of 200 messages could in
 * principle carry a thousand. The cap is a cost control rather than a correctness claim, and a
 * client at that volume has a fuller conversation than the 200-message window shows anyway.
 */
export function useChatAttachments(
  tenantId: string | null
): UseQueryResult<AttachmentsByParent> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, chatAttachmentsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        // The gateway answers the newest 500 newest first, so it is reversed as the client's read is.
        const rows = await viewRows<ClientAttachment>("admin_as_client_attachments", { p_tenant_id: source.tenantId });
        return groupBy(rows.reverse(), (row) => row.message_id);
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("attachments")
        .select(ATTACHMENT_COLUMNS)
        .not("message_id", "is", null)
        // The newest 500, the files of the messages on screen, then back in the order they were
        // added. It was the oldest 500 until 2026-09-25, so past 500 files the newest message's
        // file would have been the one left out. Nordvik Bygg holds 401 chat files on staging, all
        // from the file suite, so the edge was a run or two away (build round 2, task B).
        .order("created_at", { ascending: false })
        .limit(500);

      if (error) {
        throw error;
      }
      return groupBy(
        ((data ?? []) as unknown as ClientAttachment[]).reverse(),
        (row) => row.message_id
      );
    }
  });
}

/**
 * Every file hanging off one request's comments.
 *
 * `public.attachments` has no `request_id`. It reaches a request only through
 * `request_comment_id`, which is why this takes the thread's comment ids rather than the request's
 * own id, and why it is disabled until the thread has arrived. The request id is in the key so the
 * cache is per request, and the ids are in the key as well so that a comment added a moment ago
 * changes the key and the files that were just uploaded to it are actually fetched.
 *
 * `.in()` with an empty array would be a filter matching nothing, which is the same answer as not
 * asking. `enabled` keeps it from asking at all.
 */
export function useRequestAttachments(
  requestId: string | null,
  commentIds: readonly string[]
): UseQueryResult<AttachmentsByParent> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, [...requestAttachmentsKey(requestId ?? "none"), commentIds.join(",")]),
    enabled: Boolean(requestId) && commentIds.length > 0,
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<ClientAttachment>("admin_as_client_attachments", {
          p_tenant_id: source.tenantId,
          p_request_comment_ids: [...commentIds]
        });
        return groupBy(rows, (row) => row.request_comment_id);
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("attachments")
        .select(ATTACHMENT_COLUMNS)
        .in("request_comment_id", [...commentIds])
        .order("created_at", { ascending: true })
        .limit(500);

      if (error) {
        throw error;
      }
      return groupBy(
        (data ?? []) as unknown as ClientAttachment[],
        (row) => row.request_comment_id
      );
    }
  });
}

/* -------------------------------------------------------------------------- */
/* 3. Vägen till Workern                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The Worker is same-origin, and this is a fourth copy of the same helper.
 *
 * `architecture.md` section 6 has the Worker serve both `/api/*` and the application itself through
 * its ASSETS binding, so a relative path is the whole address and there is no base URL to configure
 * or to get wrong. `app/data/documentQueries.ts`, `app/data/adminClientQueries.ts` and
 * `app/data/adminDocumentWriteQueries.ts` each carry one, every one of them module-private, and the
 * right home is `app/lib`. There is still no shared module there and creating one is not this
 * round's work, so this copy is declared as a copy rather than smuggled in as an original.
 *
 * What is copied from `adminDocumentWriteQueries.ts` specifically, rather than from the two older
 * ones, is the throw. The old copies discard the Worker's `message` and throw the code alone, which
 * works for them because every refusal they can meet is a `PORTAL_*` code that `app/lib/errors.ts`
 * names. This path cannot rely on that: `upload_too_large`, `content_type_not_allowed`,
 * `content_signature_mismatch`, `content_type_archive` and `object_missing` are the Worker's own
 * lowercase vocabulary, and `toPortalFailure` matches `/PORTAL_[A-Z_]+/`, so every one of them would
 * render as "Något gick fel. Försök igen." The sentence beside the code is carried along and used
 * when, and only when, `app/lib/errors.ts` has nothing to say.
 */
class WorkerRefusal extends Error {
  readonly sentence: string | null;

  constructor(code: string, sentence: string | null) {
    super(code);
    this.name = "WorkerRefusal";
    this.sentence = sentence;
  }
}

interface WorkerErrorBody {
  error?: string;
  message?: string;
}

async function callWorker(path: string, init: RequestInit): Promise<Response> {
  const supabase = getSupabaseBrowserClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    throw new Error("PORTAL_UNAUTHENTICATED");
  }

  const response = await fetch(path, {
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` }
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as WorkerErrorBody | null;

    throw new WorkerRefusal(
      body?.error ?? `PORTAL_UPLOAD_FAILED_${response.status}`,
      body?.message ?? null
    );
  }

  return response;
}

/**
 * The failure, said in the one vocabulary the screen is allowed to show.
 *
 * `app/lib/errors.ts` wins whenever it has an answer, including `PORTAL_OFFLINE`, which it decides
 * from the shape of the failure rather than from a code anybody sent. Only a refusal it collapses to
 * `PORTAL_UNEXPECTED` falls through to the Worker's own sentence, and only when there is one.
 */
function toAttachmentFailure(error: unknown): PortalFailure {
  const failure = toPortalFailure(error);

  if (failure.code === PORTAL_UNEXPECTED && error instanceof WorkerRefusal && error.sentence) {
    return { code: error.message, message: error.sentence };
  }

  return failure;
}

/**
 * What `/api/files/upload-intent` answers with.
 *
 * `upload_path` is the address of the PUT, built by the Worker out of the attachment id it minted.
 * Nothing on this side composes it. `tests/worker-route-reachability.test.ts` refuses a dynamically
 * assembled `/api/` path in `app/` for exactly that reason, and the reason behind the reason is that
 * the route's shape would then live in two languages.
 *
 * `attachment_id` rather than `storage_object_id`: the attachment route mints the public row's id and
 * derives the R2 key from it, where the document route hands back the private storage object. The two
 * intents look alike and answer differently.
 */
interface UploadIntentResponse {
  attachment_id: string;
  upload_permit: string;
  upload_path: string;
  max_bytes: number;
}

/** What `/api/files/download-ticket` answers with. Same shape both gateways behind it produce. */
interface DownloadTicketResponse {
  ticket: string;
  expires_at: string;
  download_path: string;
}

/* -------------------------------------------------------------------------- */
/* 4. Uppladdningen                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Which parent the files hang from, and which list to refresh once they are on it.
 *
 * `requestId` is carried for the invalidation and for nothing else. The gateway never sees it:
 * `begin_attachment_upload` takes the comment id, and `public.attachments` has no request column.
 */
export type AttachmentParent =
  | { kind: "chat_message"; messageId: string }
  | { kind: "request_comment"; requestCommentId: string; requestId: string };

export interface UploadAttachmentsInput {
  tenantId: string;
  parent: AttachmentParent;
  files: readonly File[];
}

export interface AttachmentUploadFailure {
  file: File;
  failure: PortalFailure;
}

export interface UploadAttachmentsResult {
  /** `attachments.id` for every file that reached `available`. */
  uploaded: string[];
  /** The files that did not, each with the refusal that stopped it, in the order they were sent. */
  failed: AttachmentUploadFailure[];
}

/**
 * Intent, then bytes, once per file, and never throwing for one file.
 *
 * **Sequential rather than parallel, and that is a rate-limit decision.**
 * `begin_attachment_upload` spends `upload_intent` at 10 per 10 minutes and `upload_intent_daily` at
 * 50 per day, both keyed on the person. Five files in parallel is five calls into a bucket of ten
 * either way, so concurrency buys nothing against the ceiling; what it costs is the ordering of the
 * failures, and a person who sent four files wants to know which one was refused.
 *
 * **A per-file failure is a result rather than an exception.** The parent already exists by the time
 * this runs, because `begin_attachment_upload` requires it, so throwing for the set would throw away
 * the knowledge of which files did land. The surfaces use `failed` to keep exactly those files in
 * the composer, which is what makes a retry send three files instead of four and stay under the
 * five-per-parent ceiling.
 *
 * **`x-portal-tenant` on the PUT is not optional.** `worker/src/routes/files.ts` reads the tenant off
 * that header and passes it to `finalize_attachment_upload`, which refuses a tenant the caller may
 * not write to. Omitting it is a 400 after the bytes have been read, and the document path does not
 * need it, so it is the one thing this flow does not share with `usePublishClientDocument`.
 *
 * No digest is computed here, and that is the other difference from the document path. For contract
 * evidence the hash is the evidence and the Worker compares it against what was declared; for an
 * attachment `begin_attachment_upload` takes no hash at all and `finalize_attachment_upload` records
 * the digest of whatever arrived. There is nothing to declare, so nothing declares one.
 */
export function useUploadAttachments() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<UploadAttachmentsResult, PortalFailure, UploadAttachmentsInput>({
    mutationFn: async ({ tenantId, parent, files }) => {
      refuseInView(source);
      const uploaded: string[] = [];
      const failed: AttachmentUploadFailure[] = [];

      for (const file of files) {
        try {
          const intentResponse = await callWorker("/api/files/upload-intent", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              tenant_id: tenantId,
              display_filename: file.name,
              media_type: file.type,
              byte_size: file.size,
              message_id: parent.kind === "chat_message" ? parent.messageId : null,
              request_comment_id:
                parent.kind === "request_comment" ? parent.requestCommentId : null
            })
          });

          const intent = (await intentResponse.json()) as UploadIntentResponse;

          // The path the Worker built, never one assembled here out of the attachment id.
          await callWorker(intent.upload_path, {
            method: "PUT",
            headers: {
              "content-type": file.type,
              "x-upload-permit": intent.upload_permit,
              "x-portal-tenant": tenantId
            },
            body: file
          });

          uploaded.push(intent.attachment_id);
        } catch (error) {
          failed.push({ file, failure: toAttachmentFailure(error) });
        }
      }

      return { uploaded, failed };
    },
    onSuccess: (_result, variables) => {
      if (variables.parent.kind === "chat_message") {
        void queryClient.invalidateQueries({ queryKey: chatAttachmentsKey(variables.tenantId) });
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: requestAttachmentsKey(variables.parent.requestId)
      });
    }
  });
}

/* -------------------------------------------------------------------------- */
/* 4b. Filerna tillsammans med det de hör till                                 */
/* -------------------------------------------------------------------------- */

/**
 * What a file travels with, when the parent does not exist yet.
 *
 * `useUploadAttachments` above needs a parent that already exists, which is why a new ärende, a
 * reopening and a message that is only a screenshot could not carry a file at all. These four are
 * sent to `/api/files/compose` instead: the Worker mints an id and an object key per file, the
 * gateway writes the parent and admits the files in one transaction, and the bytes follow through
 * the same upload route as before. `supabase/migrations/20260913000003_…` argues the shape.
 */
export type ComposeParent =
  | { kind: "chat_message"; body: string | null; idempotencyKey: string }
  | {
      kind: "request";
      serviceId: string;
      title: string | null;
      description: string | null;
      requestType: string;
      idempotencyKey: string;
    }
  | { kind: "request_comment"; requestId: string; body: string | null; idempotencyKey: string }
  | { kind: "request_reopen"; requestId: string; body: string | null };

export interface ComposeInput {
  tenantId: string;
  parent: ComposeParent;
  files: readonly File[];
}

export interface ComposeResult {
  /** The message, the request or the comment that now exists. For a reopening, the request. */
  parentId: string;
  /** Set for a new request, so the screen can open it. */
  requestNumber: number | null;
  uploaded: string[];
  /** Files the parent carries a row for and whose bytes did not arrive, each with its refusal. */
  failed: AttachmentUploadFailure[];
}

interface ComposeResponse {
  parent_id: string;
  request_number: number | null;
  uploads: { index: number; attachment_id: string; upload_permit: string; upload_path: string }[];
  not_admitted: number[];
}

/**
 * The parent and its files, in one call, then the bytes one file at a time.
 *
 * **A refusal before anything was written throws; a file that fails afterwards is a result.** The
 * first call is one transaction, so a refused tenant, an empty message or a sixth file leaves nothing
 * behind and the composer keeps everything as it was. Once it answers, the message or the ärende
 * exists, so a file whose upload fails is reported beside it rather than thrown for the set, exactly
 * as `useUploadAttachments` does.
 *
 * **Sequential uploads, for the reason given above that hook:** the rate limit was already spent by
 * the gateway once per file, and the order of the failures is the thing a person reads.
 */
export function useComposeWithAttachments() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<ComposeResult, PortalFailure, ComposeInput>({
    mutationFn: async ({ tenantId, parent, files }) => {
      refuseInView(source);
      const body: Record<string, unknown> = {
        tenant_id: tenantId,
        kind: parent.kind,
        files: files.map((file) => ({
          display_filename: file.name,
          media_type: file.type,
          byte_size: file.size
        }))
      };

      if (parent.kind === "request") {
        Object.assign(body, {
          service_id: parent.serviceId,
          title: parent.title,
          description: parent.description,
          request_type: parent.requestType,
          idempotency_key: parent.idempotencyKey
        });
      } else if (parent.kind === "request_reopen") {
        Object.assign(body, { request_id: parent.requestId, body: parent.body });
      } else if (parent.kind === "request_comment") {
        Object.assign(body, {
          request_id: parent.requestId,
          body: parent.body,
          idempotency_key: parent.idempotencyKey
        });
      } else {
        Object.assign(body, { body: parent.body, idempotency_key: parent.idempotencyKey });
      }

      let composed: ComposeResponse;

      try {
        const response = await callWorker("/api/files/compose", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body)
        });
        composed = (await response.json()) as ComposeResponse;
      } catch (error) {
        throw toAttachmentFailure(error);
      }

      const uploaded: string[] = [];
      const failed: AttachmentUploadFailure[] = [];

      for (const index of composed.not_admitted) {
        failed.push({
          file: files[index],
          failure: {
            code: "PORTAL_ATTACHMENT_NOT_ADMITTED",
            message: "Filen kom inte med. Bifoga den igen."
          }
        });
      }

      for (const upload of composed.uploads) {
        const file = files[upload.index];

        try {
          // The path the Worker built, never one assembled here out of the attachment id.
          await callWorker(upload.upload_path, {
            method: "PUT",
            headers: {
              "content-type": file.type,
              "x-upload-permit": upload.upload_permit,
              "x-portal-tenant": tenantId
            },
            body: file
          });
          uploaded.push(upload.attachment_id);
        } catch (error) {
          failed.push({ file, failure: toAttachmentFailure(error) });
        }
      }

      return {
        parentId: composed.parent_id,
        requestNumber: composed.request_number,
        uploaded,
        failed
      };
    },
    // Returned since build round 56, 2026-11-02: the composer clears and the sheet says done only once
    // the message or request is in the read that shows it, never while it is still missing there.
    onSuccess: (_result, variables) => {
      const { parent, tenantId } = variables;

      if (parent.kind === "chat_message") {
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: chatMessagesKey(tenantId) }),
          queryClient.invalidateQueries({ queryKey: chatAttachmentsKey(tenantId) })
        ]);
      }

      return Promise.all([
        queryClient.invalidateQueries({ queryKey: requestsKey(tenantId) }),
        ...(parent.kind !== "request"
          ? [
              queryClient.invalidateQueries({ queryKey: requestThreadKey(parent.requestId) }),
              queryClient.invalidateQueries({ queryKey: requestAttachmentsKey(parent.requestId) })
            ]
          : [])
      ]);
    }
  });
}

/* -------------------------------------------------------------------------- */
/* 5. Nedladdningen                                                            */
/* -------------------------------------------------------------------------- */

export interface AttachmentDownloadInput {
  tenantId: string;
  attachmentId: string;
  /** What the file is saved as. `display_filename` from the same row the control sits on. */
  filename: string;
}

/**
 * Ticket, then bytes, then a save.
 *
 * **The body carries `tenant_id`, and that presence is what selects the client gateway.** The Worker
 * reads the absence of that field as the admin case: Petrovic is a member of no tenant, so
 * `public.current_tenant_ids()` is empty for him and `public.issue_attachment_download_ticket`
 * refuses him before it looks at the file. A client names the tenant they are asking as, because a
 * person with two memberships must not be able to fetch tenant A's file under tenant B's name.
 * `app/data/adminClientQueries.ts` documents the same fact from the other side, and the two comments
 * are the two halves of one rule rather than a duplicate.
 *
 * Neither branch decides authorisation. Both gateways refuse on their own terms, and
 * `tests/db/phase7-client-attachments.test.ts` proves the client half of that on the server rather
 * than in the view.
 *
 * The second request has to be a `fetch` rather than a navigation, because the Worker authenticates
 * it from the `Authorization` header and a browser navigation carries no header. So the file arrives
 * as a blob and is handed over through an object URL that is revoked immediately: the URL is a handle
 * to memory in this tab, not a second way to reach the file. plan.md section 14.5 requires the
 * browser never to receive a reusable R2 URL, bucket name or object key, and nothing here has one to
 * leak. The Worker already sets `Content-Disposition: attachment`, so the bytes cannot render inline
 * in the portal's own origin either.
 *
 * The ticket lives 45 seconds and is single use, which is why `PORTAL_TICKET_EXPIRED` and
 * `PORTAL_TICKET_CONSUMED` are ordinary answers on a slow connection rather than faults, and why
 * `app/lib/errors.ts` answers all three ticket codes with "Tryck Öppna igen."
 *
 * Nothing is invalidated on success. Reading a file changes no row this screen shows: the attachment
 * list reports no download count and no last-read timestamp, so a refetch would return the same rows.
 * The audit trail is the record that it happened, and it is the database's rather than this screen's.
 */
export function useAttachmentDownload() {
  const source = useReadSource();
  return useMutation<string, PortalFailure, AttachmentDownloadInput>({
    mutationFn: async ({ tenantId, attachmentId, filename }) => {
      refuseInView(source);
      let blob: Blob;

      try {
        const ticketResponse = await callWorker("/api/files/download-ticket", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ tenant_id: tenantId, attachment_id: attachmentId })
        });

        const ticket = (await ticketResponse.json()) as DownloadTicketResponse;
        // The path the Worker built out of the ticket it just minted, not one composed here.
        const fileResponse = await callWorker(ticket.download_path, { method: "GET" });
        blob = await fileResponse.blob();
      } catch (error) {
        throw toAttachmentFailure(error);
      }

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      return attachmentId;
    }
  });
}
